<template>
    <mobile-template title="Planning">

        <template #header-left>
            <div>
                <p class="text-xl font-bold text-gray-900">Planning</p>
                <p class="text-xs font-semibold text-gray-400">
                    {{ events.length }} chantier{{ events.length > 1 ? 's' : '' }} aujourd'hui
                </p>
            </div>
        </template>

        <div class="bg-gray-950 select-none fixed top-0 left-0 right-0 z-20 shadow-sm">
            <div class="flex items-center gap-2 px-4 pt-4 pb-3">
                <button class="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 active:bg-white/20 transition-colors shrink-0" @click="prevWeek">
                    <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round" /></svg>
                </button>
                
                <div class="flex-1">
                    <div class="flex justify-between gap-1">
                        <button v-for="day in openWeekDays" :key="day.date" class="flex flex-col items-center px-3 py-2 rounded-xl transition-all shrink-0" :class="selectedDate === day.date ? 'bg-orange-500 shadow-md shadow-orange-500/20' : 'bg-white/5'" @click="selectDate(day.date)">
                            <span class="text-[9px] font-bold tracking-wider uppercase" :class="selectedDate === day.date ? 'text-white' : 'text-white/40'">{{ day.label }}</span>
                            <span class="text-lg font-extrabold mt-0.5" :class="selectedDate === day.date ? 'text-white' : 'text-white/90'">{{ day.day }}</span>
                        </button>
                    </div>
                </div>

                <button class="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 active:bg-white/20 transition-colors shrink-0" @click="nextWeek">
                    <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round" /></svg>
                </button>
            </div>
        </div>

        <div class="p-4 space-y-6 bg-gray-50 min-h-screen pt-[115px] pb-24">

            <!-- SECTION : Le Matin -->
            <div class="space-y-4">
                
                <div class="bg-amber-400 text-amber-950 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-xl bg-white/30 flex items-center justify-center backdrop-blur-sm">
                            <i class="fas fa-mountain-sun text-amber-900 text-lg"></i>
                        </div>
                        <div>
                            <h2 class="text-lg font-black tracking-tight uppercase leading-tight">Le Matin</h2>
                        </div>
                    </div>
                    <span v-if="events.filter(e => e.momentDay === 'morning').length > 0" class="text-xs font-black px-3 py-1 bg-black/10 rounded-full">
                        {{ events.filter(e => e.momentDay === 'morning').length }}
                    </span>
                </div>

                <div v-if="events.filter(e => e.momentDay === 'morning').length === 0" class="bg-white rounded-2xl p-5 text-center border border-gray-100 shadow-sm">
                    <p class="text-xs text-gray-400 font-medium">Aucun chantier planifié ce matin</p>
                </div>

                <div 
                    v-for="item in events.filter(e => e.momentDay === 'morning')" 
                    :key="item.id"
                    class="rounded-2xl border shadow-sm active:scale-[0.99] transition-all overflow-hidden"
                    :class="[
                        item.replan ? 'bg-red-50 border-red-200 opacity-75' : 'bg-white border-gray-100',
                        item.statutCode === 'en_cours' && !item.replan ? 'ring-2 ring-amber-400' : '',
                        item.statutCode === 'termine' && !item.replan ? 'bg-green-50 border-green-200 opacity-50' : ''
                    ]"
                >
                    <div class="p-4 space-y-3.5">
                        <div v-if="item.statutCode === 'termine' || item.replan" class="flex justify-between items-center">
                            <span v-if="item.replan" class="bg-red-100 text-red-700 px-2.5 py-1 rounded-lg font-black text-[10px] uppercase tracking-wider flex items-center gap-1">
                                <span>Replanifié / Annulé</span>
                            </span>
                            <span v-else-if="item.statutCode === 'termine'" class="bg-green-100 text-green-700 px-2.5 py-1 rounded-lg font-black text-[10px] uppercase tracking-wider flex items-center gap-1">
                                <span>Terminé</span>
                            </span>
                        </div>

                        <div>
                            <h3 class="text-lg font-bold text-gray-900 tracking-tight uppercase flex items-center gap-1.5">
                                <i v-if="item.statutCode !== 'termine' && !item.replan" class="fas fa-user text-gray-400 text-sm shrink-0"></i> 
                                {{ item.client || 'Client Non Renseigné' }}
                            </h3>

                            <div class="flex items-start gap-2 rounded-xl border" :class="[item.statutCode === 'termine' || item.replan ? 'mt-2 border-transparent' : 'bg-gray-50/80 p-2.5 mt-2 border-gray-100']">
                                <svg v-if="item.statutCode !== 'termine' && !item.replan" class="w-4 h-4 text-gray-400 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                                </svg> 
                                <span class="text-xs font-semibold text-gray-600 leading-normal">{{ item.address }}</span>
                            </div>

                            <!-- Motif de replanification -->
                            <div v-if="item.replan && item.replanReason" class="mt-2 p-2.5 bg-red-100/60 border border-red-200 rounded-xl flex flex-col gap-0.5">
                                <span class="text-[10px] font-bold uppercase text-red-800 tracking-wide">Motif du report :</span>
                                <span class="text-xs font-semibold text-red-950">{{ item.replanReason }}</span>
                            </div>
                        </div>

                        <button 
                            v-if="item.statutCode !== 'termine' && !item.replan"
                            @click="$router.push(`/chantier/${item.chantierId}/e/${item.id}/list`)"
                            class="w-full py-3 rounded-xl font-bold text-sm tracking-wide transition-all flex items-center justify-center gap-2 shadow-sm"
                            :class="[
                                item.replan ? 'bg-gray-200 text-gray-500 active:bg-gray-300 line-through' : '',
                                item.statutCode === 'en_cours' && !item.replan ? 'bg-amber-400 text-amber-950 active:bg-amber-500' : '',
                                item.statutCode !== 'en_cours' && !item.replan ? 'bg-gray-900 text-white active:bg-black' : ''
                            ]"
                        >
                            <span>
                                {{ item.statutCode === 'en_cours' ? 'Reprendre le chantier' : 'Ouvrir la fiche' }}
                            </span>
                            <svg class="w-4 h-4 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        </button>
                    </div>
                </div>
            </div>

            <!-- SECTION : L'Après-midi -->
            <div class="space-y-4 pt-2">
                
                <div class="bg-sky-400 text-sky-950 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-xl bg-white/30 flex items-center justify-center backdrop-blur-sm">
                            <i class="fas fa-sun text-sky-900 text-lg"></i>
                        </div>
                        <div>
                            <h2 class="text-lg font-black tracking-tight uppercase leading-tight">L'Après-midi</h2>
                        </div>
                    </div>
                    <span v-if="events.filter(e => e.momentDay === 'afternoon').length > 0" class="text-xs font-black px-3 py-1 bg-black/10 rounded-full">
                        {{ events.filter(e => e.momentDay === 'afternoon').length }}
                    </span>
                </div>

                <div v-if="events.filter(e => e.momentDay === 'afternoon').length === 0" class="bg-white rounded-2xl p-5 text-center border border-gray-100 shadow-sm">
                    <p class="text-xs text-gray-400 font-medium">Aucun chantier planifiée cet après-midi</p>
                </div>

                <div 
                    v-for="item in events.filter(e => e.momentDay === 'afternoon')" 
                    :key="item.id"
                    class="rounded-2xl border shadow-sm active:scale-[0.99] transition-all overflow-hidden"
                    :class="[
                        item.replan ? 'bg-red-50 border-red-200 opacity-75' : 'bg-white border-gray-100',
                        item.statutCode === 'en_cours' && !item.replan ? 'ring-2 ring-sky-400' : '',
                        item.statutCode === 'termine' && !item.replan ? 'bg-green-50 border-green-200 opacity-50' : ''
                    ]"
                >
                    <div class="p-4 space-y-3.5">
                        <div v-if="item.statutCode === 'termine' || item.replan" class="flex justify-between items-center">
                            <span v-if="item.replan" class="bg-red-100 text-red-700 px-2.5 py-1 rounded-lg font-black text-[10px] uppercase tracking-wider flex items-center gap-1">
                                <span>Replanifié / Annulé</span>
                            </span>
                            <span v-else-if="item.statutCode === 'termine'" class="bg-green-100 text-green-700 px-2.5 py-1 rounded-lg font-black text-[10px] uppercase tracking-wider flex items-center gap-1">
                                <span>Terminé</span>
                            </span>
                        </div>

                        <div>
                            <h3 class="text-lg font-bold text-gray-900 tracking-tight uppercase flex items-center gap-1.5">
                                <i v-if="item.statutCode !== 'termine' && !item.replan" class="fas fa-user text-gray-400 text-sm shrink-0"></i> 
                                {{ item.client || 'Client Non Renseigné' }}
                            </h3>

                            <div class="flex items-start gap-2 rounded-xl border" :class="[item.statutCode === 'termine' || item.replan ? 'mt-2 border-transparent' : 'bg-gray-50/80 p-2.5 mt-2 border-gray-100']">
                                <svg v-if="item.statutCode !== 'termine' && !item.replan" class="w-4 h-4 text-gray-400 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                                </svg> 
                                <span class="text-xs font-semibold text-gray-600 leading-normal">{{ item.address }}</span>
                            </div>

                            <!-- Motif de replanification -->
                            <div v-if="item.replan && item.replanReason" class="mt-2 p-2.5 bg-red-100/60 border border-red-200 rounded-xl flex flex-col gap-0.5">
                                <span class="text-[10px] font-bold uppercase text-red-800 tracking-wide">Motif du report :</span>
                                <span class="text-xs font-semibold text-red-950">{{ item.replanReason }}</span>
                            </div>
                        </div>

                        <button 
                            v-if="item.statutCode !== 'termine' && !item.replan"
                            @click="$router.push(`/chantier/${item.chantierId}/list`)"
                            class="w-full py-3 rounded-xl font-bold text-sm tracking-wide transition-all flex items-center justify-center gap-2 shadow-sm"
                            :class="[
                                item.replan ? 'bg-gray-200 text-gray-500 active:bg-gray-300 line-through' : '',
                                item.statutCode === 'en_cours' && !item.replan ? 'bg-amber-400 text-amber-950 active:bg-amber-500' : '',
                                item.statutCode !== 'en_cours' && !item.replan ? 'bg-gray-900 text-white active:bg-black' : ''
                            ]"
                        >
                            <span>
                                {{ item.statutCode === 'en_cours' ? 'Reprendre le chantier' : 'Ouvrir la fiche' }}
                            </span>
                            <svg class="w-4 h-4 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        </button>
                    </div>
                </div>
            </div>

        </div>
    </mobile-template>
</template>

<script>
const DAY_NAME_TO_JS = {
    dimanche: 0, lundi: 1, mardi: 2, mercredi: 3,
    jeudi: 4, vendredi: 5, samedi: 6,
};

export default {
    name: 'PlanningView',

    data() {
        // 1. On regarde si on a une date sauvegardée
        const savedDate = localStorage.getItem('planning_selected_date');
        // 2. Si oui, on prend la sauvegardée. Si non, on prend la date d'aujourd'hui
        const initialDate = savedDate || this.formatDateLocal(new Date());
        
        return {
            selectedDate: initialDate,
            // 3. On cale les boutons de la semaine sur le lundi de la date sélectionnée
            weekStart: this.getMonday(new Date(initialDate)),
            events: [],
            loading: false,
            currentRequestId: 0,
        };
    },

    computed: {
        openDayNumbers() {
            const raw = this.$account?.account?.societe?.open_days ?? [];
            return raw.map(n => DAY_NAME_TO_JS[n.toLowerCase()]).filter(n => n !== undefined);
        },

        openWeekDays() {
            const todayISO = this.getTodayISO();
            const days = [];
            for (let i = 0; i < 7; i++) {
                const d = new Date(this.weekStart);
                d.setDate(d.getDate() + i);
                if (!this.openDayNumbers.includes(d.getDay())) continue;
                const iso = this.formatDateLocal(d);
                days.push({
                    date: iso,
                    day: d.getDate(),
                    label: d.toLocaleDateString('fr-FR', { weekday: 'short' }).toUpperCase().slice(0, 3),
                    isToday: iso === todayISO,
                });
            }
            return days;
        },

        weekLabel() {
            const start = new Date(this.weekStart);
            const end = new Date(this.weekStart);
            end.setDate(end.getDate() + 6);
            return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
        },

        isCurrentWeek() {
            return this.formatDateLocal(this.weekStart) === this.formatDateLocal(this.getMonday(new Date()));
        },
    },

    watch: {
        selectedDate(newDate) { 
            // Dès que la date change (clic ou switch de semaine), on la mémorise
            localStorage.setItem('planning_selected_date', newDate);
            this.loadEvents(); 
        },
        openWeekDays(days) {
            if (!days.find(d => d.date === this.selectedDate)) {
                this.selectedDate = days[0]?.date ?? this.selectedDate;
            }
        },
    },

    created() {
        this.loadEvents();
    },

    methods: {
        async loadEvents() {
            const userId = this.$account.account.id;
            if (!userId) return;
            const requestId = ++this.currentRequestId;
            this.loading = true;
            try {
                const response = await this.$request.post('/interventions/today', {
                    technicien_id: userId,
                    date: this.selectedDate,
                });
                if (requestId !== this.currentRequestId) return;

                this.events = Array.isArray(response) ? response.map(this.mapEvent) : [];
            } catch (error) {
                if (requestId !== this.currentRequestId) return;
                this.$notify?.error(error?.message || 'Impossible de charger le planning');
            } finally {
                if (requestId === this.currentRequestId) this.loading = false;
            }
        },

        mapEvent(item) {
            const base = {
                id: item.id,
                type: item.type,
                start: item.heure_debut ? item.heure_debut.substring(0, 5).replace(':', 'h') : '',
                end: item.heure_fin ? item.heure_fin.substring(0, 5).replace(':', 'h') : '',
                estimation: item.estimation,
                momentDay: item.moment_day,
                client: `${item.chantier?.client?.nom ?? ''} ${item.chantier?.client?.prenom ?? ''}`.trim(),
                clientPhone: item.chantier?.client?.telephone ?? null,
                address: item.chantier?.adresse ? `${item.chantier?.adresse.adresse1}, ${item.chantier?.adresse.code_postal} ${item.chantier?.adresse.ville}` : null,
                chantierId: item.chantier_id,
                chantierStatutId: item.chantier?.statut_id ?? null,
                statutCode: item.chantier.statut_id == 1 || item.chantier.statut_id == 2 ? null : 'termine',
                replan: item.replan,
                replanReason: item.replan_reason,
            };

            console.log(base.statutCode);
            
            return base;
        },

        selectDate(date) {
            this.selectedDate = date;
        },
        prevWeek() {
            const d = new Date(this.weekStart);
            d.setDate(d.getDate() - 7);
            this.weekStart = d;
            
            // Pour le confort des gars : quand ils changent de semaine, 
            // on sélectionne automatiquement le premier jour de travail dispo de cette nouvelle semaine
            this.$nextTick(() => {
                if (this.openWeekDays.length > 0) {
                    this.selectedDate = this.openWeekDays[0].date;
                }
            });
        },
        nextWeek() {
            const d = new Date(this.weekStart);
            d.setDate(d.getDate() + 7);
            this.weekStart = d;

            // Pareil ici, sélection auto du jour pour éviter un écran blanc
            this.$nextTick(() => {
                if (this.openWeekDays.length > 0) {
                    this.selectedDate = this.openWeekDays[0].date;
                }
            });
        },
        goToCurrentWeek() {
            this.weekStart = this.getMonday(new Date());
            this.selectedDate = this.getTodayISO();
        },
        getTodayISO() { return this.formatDateLocal(new Date()); },
        getMonday(date) {
            const d = new Date(date);
            d.setHours(12, 0, 0, 0);
            const day = d.getDay() || 7;
            d.setDate(d.getDate() - day + 1);
            return d;
        },
        formatDateLocal(date) {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        },
        formatDuration(minutes) {
            if (!minutes) return '–';
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`;
        },
        formatMomentDay(moment) {
            if (moment === 'morning') return 'Matin';
            if (moment === 'afternoon') return 'Après-midi';
            if (moment === 'full_day') return 'Journée';
            return moment || '–';
        },
    },
};
</script>